'use client';
import { withSSRAuth, getServerSideAuthProps } from '@/lib/withSSRAuth';
import type { GetServerSideProps } from 'next';
import { useState, useEffect, Suspense } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Calendar } from "lucide-react";
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from "@/lib/utils";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { toast } from "sonner";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import dynamic from "next/dynamic";
const JobMapClient = dynamic(() => import("@/components/JobMap"), {
  ssr: false,
  suspense: true,
});
const JobFilterForm = dynamic(() => import("@/components/JobFilterForm"), {
  ssr: false,
  suspense: true,
});
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";


const Jobs = () => {
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [selectedPicture, setSelectedPicture] = useState<string | null>(null);
  const formatJobType = (type: string) =>
    type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  const formatShortAddress = (address: string) => {
    const parts = address.split(',').map((p) => p.trim());
    if (parts.length >= 2) {
      const country = parts.pop();
      const district = parts.pop();
      return `${district}, ${country}`;
    }
    return address;
  };
  const [userName, setUserName] = useState<string>('');
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const handleResetFilters = () => router.push(pathname);
  const pageParam = searchParams.get("page") || "1";
  const currentPage = parseInt(pageParam, 10);
  const [jobs, setJobs] = useState<any[]>([]);
  const [metadata, setMetadata] = useState<{ hasNextPage: boolean; totalPages: number }>({
    hasNextPage: false,
    totalPages: 1,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetch('/api/user')
      .then((res) => res.json())
      .then((data) => {
        if (data?.name) {
          setUserName(data.name);
        }
        setStripeAccountId(data?.stripeAccountId ?? null);
      })
      .catch((err) => console.error('Error fetching user:', err));
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const filterString = searchParams.toString();
    const url = filterString
      ? `/api/jobs?page=${currentPage}&${filterString}`
      : `/api/jobs?page=${currentPage}`;
    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Error fetching jobs: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then((data) => {
        setJobs(data.jobs);
        setMetadata(data.metadata);
      })
      .catch((err) => {
        console.error('Error fetching jobs:', err);
      })
      .finally(() => setIsLoading(false));
  }, [currentPage, searchParams]);

  if (isLoading) {
    return (
      <main className="flex-1 p-6 md:p-10 ml-0 md:ml-64">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-10 w-48 mb-6" />
          <div className="flex space-x-4 mb-6">
            <Skeleton className="h-8 w-32 rounded-full" />
            <Skeleton className="h-8 w-32 rounded-full" />
          </div>
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, idx) => (
              <Skeleton key={idx} className="h-48 w-full" />
            ))}
          </div>
        </div>
      </main>
    );
  }
  return (
    <>
      <main className="flex-1 p-6 md:p-10 ml-0 md:ml-64">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="mb-8">
            <h1 className="text-3xl font-semibold tracking-tight">
              {userName ? `Welcome, ${userName}` : 'Available jobs'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Find work that fits your schedule. Filter by distance, type, and urgency.
            </p>
          </header>

          {/* Filters */}
          <div className="mb-8">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">Filter Jobs</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Filter jobs</DialogTitle>
                  <DialogDescription>
                    Narrow results by location, distance, vehicle type, and size.
                  </DialogDescription>
                </DialogHeader>
                <Suspense fallback={<Skeleton className="h-64 w-full" />}>
                  <JobFilterForm />
                </Suspense>
              </DialogContent>
            </Dialog>
          </div>

          {/* Jobs List */}
          {jobs.length === 0 && searchParams.toString() && (
            <Card className="border-dashed">
              <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                <div className="bg-gray-100 p-4 rounded-full mb-4">
                  <MapPin size={32} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-medium mb-2">No jobs in this area</h3>
                <p className="text-muted-foreground mb-4">
                  We couldn&apos;t find any jobs in {searchParams.get('location')}
                </p>
                <Button onClick={handleResetFilters}>Reset Filters</Button>
              </CardContent>
            </Card>
          )}
          <div className="space-y-4">
          {jobs.map((job) => {
            let distanceMiles: number | null = null;
            if (
              job.fromLat != null && job.fromLng != null &&
              job.toLat != null && job.toLng != null
            ) {
              const toRad = (deg: number) => (deg * Math.PI) / 180;
              const R = 6371; // Earth radius in kilometers
              const dLat = toRad(job.toLat - job.fromLat);
              const dLon = toRad(job.toLng - job.fromLng);
              const lat1 = toRad(job.fromLat);
              const lat2 = toRad(job.toLat);
              const a =
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
              const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
              const dKm = R * c;
              distanceMiles = dKm * 0.621371;
            }
            const formattedFrom = typeof job.fromAddress === 'string'
              ? formatShortAddress(job.fromAddress)
              : `${job.fromAddress.city} ${job.fromAddress.postcode.slice(0, 4)}, UK`;
            const formattedTo = typeof job.toAddress === 'string'
              ? formatShortAddress(job.toAddress)
              : `${job.toAddress.city} ${job.toAddress.postcode.slice(0, 4)}, UK`;
            return (
              <Card 
                key={job.id}
                className={`transition-all hover:shadow-md cursor-pointer ${
                  selectedJob === job.id ? "border-primary ring-1 ring-primary" : ""
                }`}
                onClick={() => setSelectedJob(job.id === selectedJob ? null : job.id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg text-black">{job.title}</CardTitle>
                        {job.isUrgent && <Badge variant="destructive">ASAP</Badge>}
                      </div>
                      <span className="text-lg text-black">
                        {formatJobType(job.type).replace(/[()]/g, '')}
                      </span>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-muted-foreground">Client budget</span>
                      <span className="text-lg font-semibold">{formatCurrency(job.budget)}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Dynamic interactive map */}
                  {job.fromLat != null && job.fromLng != null && job.toLat != null && job.toLng != null && (
                    <div
                      className="h-40 w-full rounded overflow-hidden mb-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="h-full w-full pointer-events-none">
                        <Suspense fallback={<Skeleton className="h-full w-full" />}>
                          <JobMapClient
                            fromLat={job.fromLat}
                            fromLng={job.fromLng}
                            toLat={job.toLat}
                            toLng={job.toLng}
                          />
                        </Suspense>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin size={16} className="text-black mt-1" />
                        <div className="flex flex-col">
                          <span className="font-medium">Pickup:</span>
                          <span>{formattedFrom}</span>
                          <span className="font-medium mt-2">Delivery:</span>
                          <span>{formattedTo}</span>
                        </div>
                      </div>
            {/* Job-specific details */}
            {job.type === 'RECOVERY' && (
              <div className="flex items-start gap-2 text-sm">
                <span className="font-medium">Make & Model:</span>
                <span>{job.carMake} {job.carModel}</span>
              </div>
            )}
            {(job.type === 'LOGISTICS' || job.type === 'PALLETE' || job.type === 'NONSTANDARD') && (
              <div className="flex items-start gap-4 text-sm">
                <span className="font-medium">Weight:</span>
                <span>{job.weight ?? '-'}</span>
                <span className="font-medium">Quantity:</span>
                <span>{job.numberOfItems ?? '-'}</span>
              </div>
            )}
            {job.type === 'REMOVALS' && (
              <div className="flex items-start gap-4 text-sm">
                <span className="font-medium">Rooms:</span>
                <span>{job.numberOfItems}</span>
                <span className="font-medium">Garage:</span>
                <span>{job.needMan ? 'Yes' : 'No'}</span>
              </div>
            )}
            {distanceMiles != null && (
                    <div className="flex items-center gap-2 text-sm">
                          <MapPin size={14} className="text-muted-foreground" />
                          <span>{distanceMiles.toFixed(1)} miles</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <Calendar size={16} className="text-muted-foreground" />
                      <span>{job.dDate}</span>
                      <Clock size={16} className="text-muted-foreground" />
                      <span>{job.dTime}</span>
                    </div>
                    {/* Show description and Place Bid when selected */}
                    {selectedJob === job.id && (
                      <div className="mt-4 space-y-4">
                        {job.pictures && job.pictures.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {job.pictures.map((url) => (
                              <img
                                key={url}
                                src={url}
                                alt="Job upload"
                                className="h-20 w-20 object-cover rounded cursor-pointer"
                            onClick={() => setSelectedPicture(url)}
                              />
                            ))}
                          </div>
                        )}
                        <p className="text-sm text-muted-foreground">{job.description}</p>
                        <div className="flex justify-end">
                        <Button
                          className={!stripeAccountId ? 'opacity-50 cursor-not-allowed' : ''}
                          onClick={() => {
                            if (!stripeAccountId) {
                              toast.error(
                                <>
                                  You cannot place a quote until you have signed up with Stripe.&nbsp;
                                  <a href="/settings" className="underline text-primary">
                                    Go to Settings
                                  </a>
                                </>
                              );
                              return;
                            }
                            router.push(`/jobs/${job.id}/quote`);
                          }}
                        >
                          Place Quote
                        </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
          );})}
          </div>
          
          {/* Pagination */}
          <div className="mt-8">
            <Pagination>
              <PaginationContent>
                {currentPage > 1 && (
                  <PaginationItem>
                    <PaginationPrevious href={`/jobs?page=${currentPage - 1}`} />
                  </PaginationItem>
                )}
                {Array.from({ length: metadata.totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <PaginationItem key={pageNum}>
                    <PaginationLink href={`/jobs?page=${pageNum}`} isActive={pageNum === currentPage}>
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                {metadata.hasNextPage && (
                  <PaginationItem>
                    <PaginationNext href={`/jobs?page=${currentPage + 1}`} />
                  </PaginationItem>
                )}
              </PaginationContent>
            </Pagination>
          </div>
      </div>
      </main>
      <Dialog
        open={!!selectedPicture}
        onOpenChange={(open) => {
          if (!open) setSelectedPicture(null);
        }}
      >
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-4">
          <img
            src={selectedPicture || ''}
            alt="Enlarged preview"
            className="max-h-full w-auto mx-auto"
          />
          <DialogDescription className="sr-only">
            Enlarged image preview of the selected job picture.
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = getServerSideAuthProps(undefined, { role: 'USER', type: 'DRIVER' });
export default withSSRAuth(Jobs, { role: 'USER', type: 'DRIVER' });
